/**
 * Cart add-on products
 *
 * Add-on products set custom.cart_add_on_products to the main product(s)
 * they belong to. If none of those mains remain in the cart, the add-on
 * line is removed.
 */

const CartAddons = (() => {
  let isProcessing = false;

  function getCartSectionId() {
    return document.querySelector('cart-form')?.dataset?.sectionId || 'template-cart';
  }

  function parseJsonScript(root, selector, fallback) {
    const el = root.querySelector?.(selector);
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent || JSON.stringify(fallback));
    } catch (error) {
      console.error('CartAddons: invalid JSON', selector, error);
      return fallback;
    }
  }

  function getAddonMainsMap(root = document) {
    const map = parseJsonScript(root, '[data-addon-mains-map]', {});
    if (!map || typeof map !== 'object') return {};
    return Object.fromEntries(
      Object.entries(map).map(([key, list]) => [
        String(key),
        Array.isArray(list) ? list.map(String) : [],
      ]),
    );
  }

  function isAddonProduct(productId, root = document) {
    const mains = getAddonMainsMap(root)[String(productId)];
    return Array.isArray(mains) && mains.length > 0;
  }

  function setCheckoutLocked(locked) {
    document
      .querySelectorAll('[data-checkoutBtns] [name="checkout"], button#checkout, button[name="checkout"]')
      .forEach((btn) => {
        btn.disabled = locked;
        btn.classList.toggle('disabled', locked);
        btn.setAttribute('aria-disabled', locked ? 'true' : 'false');
      });
  }

  function hideCartLinesByKeys(keys) {
    const keySet = new Set(keys.filter(Boolean).map(String));
    if (!keySet.size) return;
    document.querySelectorAll('[data-cartitem-key]').forEach((el) => {
      if (!keySet.has(el.getAttribute('data-cartitem-key'))) return;
      el.classList.add('d-none', 'updating');
      el.setAttribute('aria-hidden', 'true');
    });
  }

  /**
   * Add-on lines whose listed mains are no longer in the cart (after optional
   * line keys are treated as already removed).
   */
  function planOrphanedAddonKeys(cart, excludingKeys = []) {
    const items = cart?.items || [];
    const excludedKeys = new Set(excludingKeys.filter(Boolean).map(String));
    const addonMainsMap = getAddonMainsMap(document);
    const remainingMainIds = new Set();

    items.forEach((item) => {
      if (excludedKeys.has(String(item.key))) return;
      const productId = String(item.product_id);
      if (addonMainsMap[productId]?.length) return;
      remainingMainIds.add(productId);
    });

    const removeKeys = [];
    items.forEach((item) => {
      if (excludedKeys.has(String(item.key))) return;
      const mains = addonMainsMap[String(item.product_id)];
      if (!mains?.length) return;
      const hasMain = mains.some((mainId) => remainingMainIds.has(String(mainId)));
      if (!hasMain) removeKeys.push(item.key);
    });

    return { removeKeys, remainingMainIds };
  }

  async function postCart(url, body) {
    const response = await fetch(url, {
      ...Utility.fetchConfig(),
      body: JSON.stringify(body),
    });
    const dataJSON = await response.json();
    if (dataJSON.errors) {
      Utility.handleError?.(dataJSON.errors, 'Cart Update');
      return null;
    }
    return dataJSON;
  }

  async function applyUpdates(updates, options = {}) {
    const keys = Object.keys(updates);
    if (!keys.length) return null;

    const sectionId = options.sectionId || getCartSectionId();
    hideCartLinesByKeys(keys);
    setCheckoutLocked(true);

    try {
      const dataJSON = await postCart(window.routes?.cart_update_url || '/cart/update.js', {
        updates,
        sections: sectionId,
      });
      if (!dataJSON) return null;

      if (window.globalSpace?.cart && dataJSON.item_count != null) {
        window.globalSpace.cart = {
          ...window.globalSpace.cart,
          ...dataJSON,
          items: dataJSON.items || window.globalSpace.cart.items,
          item_count: dataJSON.item_count,
        };
      }

      await refreshCart({ sections: dataJSON.sections, sectionId });
      return { ...dataJSON, sections: dataJSON.sections };
    } finally {
      setCheckoutLocked(false);
    }
  }

  async function removeOrphanedAddons(options = {}) {
    if (isProcessing) return null;

    const cart = window.globalSpace?.cart;
    if (!cart?.items?.length) return null;

    const { removeKeys } = planOrphanedAddonKeys(cart);
    if (!removeKeys.length) return null;

    isProcessing = true;
    try {
      const updates = {};
      removeKeys.forEach((key) => {
        updates[key] = 0;
      });
      return await applyUpdates(updates, options);
    } catch (error) {
      console.error('CartAddons.removeOrphanedAddons:', error);
      Utility.handleError?.(error, 'Cart Update');
      return null;
    } finally {
      isProcessing = false;
    }
  }

  async function removeMainWithLinkedAddons(parentItem, options = {}) {
    if (!parentItem?.key || !parentItem?.product_id || isProcessing) return null;

    const cart = window.globalSpace?.cart;
    if (!cart?.items?.length) return null;

    isProcessing = true;
    try {
      const { removeKeys } = planOrphanedAddonKeys(cart, [parentItem.key]);
      const updates = { [parentItem.key]: 0 };
      removeKeys.forEach((key) => {
        updates[key] = 0;
      });
      return await applyUpdates(updates, options);
    } catch (error) {
      console.error('CartAddons.removeMainWithLinkedAddons:', error);
      Utility.handleError?.(error, 'Cart Update');
      return null;
    } finally {
      isProcessing = false;
    }
  }

  function waitForCartForm(timeout = 4000) {
    return new Promise((resolve) => {
      const existing = document.querySelector('cart-form');
      if (existing && typeof existing._updateCart === 'function') {
        resolve(existing);
        return;
      }

      const start = Date.now();
      const timer = setInterval(() => {
        const el = document.querySelector('cart-form');
        if (el && typeof el._updateCart === 'function') {
          clearInterval(timer);
          resolve(el);
          return;
        }
        if (Date.now() - start > timeout) {
          clearInterval(timer);
          resolve(el || null);
        }
      }, 50);
    });
  }

  async function refreshCart({ sections, sectionId } = {}) {
    const cartForm = await waitForCartForm();
    if (!cartForm?._updateCart) return;

    const id = sectionId || cartForm.dataset.sectionId || getCartSectionId();
    if (sections) {
      cartForm._updateCart(sections);
      return;
    }

    const response = await fetch(`${window.location.pathname}?sections=${id}`);
    if (!response.ok) return;
    cartForm._updateCart(await response.json());
  }

  return {
    getAddonMainsMap,
    isAddonProduct,
    removeOrphanedAddons,
    removeMainWithLinkedAddons,
    refreshCart,
  };
})();

window.CartAddons = CartAddons;

if (document.querySelector('cart-form')) {
  CartAddons.removeOrphanedAddons();
}
